import { Component, OnInit, Input, OnChanges, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { MeteringPoint, MeteringPointReadingPoint, MeterReadingsExportParameters } from '@model';
import { Store } from '@ngrx/store';
import * as fromStore from '@appStore/metering-points';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { DatePeriodType } from '@enums';
import { MeteringPointMeteringPaneDataSource } from './metering-point-metering-pane.datasource';
import { MeteringPointService } from '@appStore/metering-points/services';
import { SxExportService } from '@services';
import { SubSink } from 'subsink';
import { ToLocalDateTimePipe } from '@utils/pipes/to-local-date-time.pipe';

@Component({
    selector: 'app-metering-point-metering-pane',
    templateUrl: './metering-point-metering-pane.component.html',
    standalone: false
})
export class MeteringPointMeteringPaneComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  meteringPoint: MeteringPoint;

  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: true })
  sort: MatSort;

  isExporting = false;

  dataSource: MeteringPointMeteringPaneDataSource;

  readings: MeteringPointReadingPoint[] = [];
  displayColumns = ['timestamp', 'quantity', 'statusName'];

  selectedMeteringPoint: MeteringPoint;
  datePeriodType: DatePeriodType = DatePeriodType.Day;

  private subs = new SubSink();

  constructor(
    private store: Store<fromStore.State>,
    private changeDetectorRef: ChangeDetectorRef,
    private meteringPointService: MeteringPointService,
    private translateService: TranslateService,
    private exportService: SxExportService,
    private toLocalDate: ToLocalDateTimePipe
  ) {}

  ngOnInit() {
    this.dataSource = new MeteringPointMeteringPaneDataSource(this.paginator, this.sort, this.readings);

    if (!this.meteringPoint) {
      return;
    }
    // Test
    if (this.meteringPoint.type !== 'LOG') {
      this.selectedMeteringPoint = this.meteringPoint;
    }

    if (
      this.meteringPoint.type !== 'LOG' &&
      !this.meteringPoint.readingsLoaded &&
      !this.meteringPoint.readingsLoading
    ) {
      this.store.dispatch(
        new fromStore.LoadMeteringPointReadingData({
          masterMeteringPointId: this.meteringPoint.id,
          meteringPointId: this.meteringPoint.id,
          resolution: DatePeriodType.Day
        })
      );
    }

    if (
      this.meteringPoint.type === 'LOG' &&
      this.meteringPoint.containedMeteringPoints &&
      this.meteringPoint.containedMeteringPoints.length > 0
    ) {
      this.selectedMeteringPoint = this.meteringPoint.containedMeteringPoints[0];

      if (!this.selectedMeteringPoint.readingsLoaded && !this.selectedMeteringPoint.readingsLoading) {
        this.store.dispatch(
          new fromStore.LoadMeteringPointReadingData({
            masterMeteringPointId: this.meteringPoint.id,
            meteringPointId: this.selectedMeteringPoint.id,
            resolution: DatePeriodType.Day
          })
        );
      } else {
        this.reloadTableData();
      }
    }
  }

  ngOnChanges() {
    if (!this.meteringPoint) {
      return;
    }

    if (this.meteringPoint.type !== 'LOG' && this.meteringPoint.readingsLoaded && this.meteringPoint.readings) {
      this.selectedMeteringPoint = this.meteringPoint;
      this.readings = this.meteringPoint.readings.map(x => ({
        ...x,
        statusName: this.translateService.instant('metering.item.metering.statuses.status' + x.status)
      }));
      this.dataSource = new MeteringPointMeteringPaneDataSource(this.paginator, this.sort, this.readings);
      this.changeDetectorRef.detectChanges();
    } else if (
      this.meteringPoint.type === 'LOG' &&
      this.meteringPoint.containedMeteringPoints &&
      this.meteringPoint.containedMeteringPoints.length > 0 &&
      this.selectedMeteringPoint
    ) {
      this.reloadTableData();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  reloadTableData = () => {
    let subMp = this.meteringPoint.containedMeteringPoints.find(x => x.id === this.selectedMeteringPoint.id);
    if (subMp && subMp.readingsLoaded && subMp.readings) {
      this.selectedMeteringPoint = subMp;
      this.readings = this.selectedMeteringPoint.readings.map(x => ({
        ...x,
        statusName: this.translateService.instant('metering.item.metering.statuses.status' + x.status)
      }));
      this.dataSource = new MeteringPointMeteringPaneDataSource(this.paginator, this.sort, this.readings);
      this.changeDetectorRef.detectChanges();
    }
  };

  onPeriodChanged = (event: MatButtonToggleChange): void => {
    this.resetPaginator();
    this.datePeriodType = event.value;

    if (this.meteringPoint.type !== 'LOG') {
      this.store.dispatch(
        new fromStore.LoadMeteringPointReadingData({
          masterMeteringPointId: this.meteringPoint.id,
          meteringPointId: this.meteringPoint.id,
          resolution: event.value
        })
      );
    } else {
      this.selectedMeteringPoint = { ...this.selectedMeteringPoint, readingsLoading: true, readingsLoaded: false };
      this.store.dispatch(
        new fromStore.LoadMeteringPointReadingData({
          masterMeteringPointId: this.meteringPoint.id,
          meteringPointId: this.selectedMeteringPoint.id,
          resolution: event.value
        })
      );
    }
  };

  resetPaginator() {
    this.paginator.pageIndex = 0;
  }

  subMeteringPointChanged = (event: MatSelectChange) => {
    this.resetPaginator();
    this.selectedMeteringPoint = this.meteringPoint.containedMeteringPoints.find(x => x.id === event.value.id);

    if (this.selectedMeteringPoint) {
      if (!this.selectedMeteringPoint.readingsLoaded && !this.selectedMeteringPoint.readingsLoading) {
        this.store.dispatch(
          new fromStore.LoadMeteringPointReadingData({
            masterMeteringPointId: this.meteringPoint.id,
            meteringPointId: this.selectedMeteringPoint.id,
            resolution: DatePeriodType.Day
          })
        );
      } else {
        this.reloadTableData();
      }
    }
  };

  export = () => {
    let exportParameters = <MeterReadingsExportParameters>{
      meteringPointId: this.selectedMeteringPoint.id,
      masterMeteringPointId: this.meteringPoint.id,
      periodType: this.datePeriodType
    };

    this.isExporting = true;
    let timestamp = this.toLocalDate.transform(new Date(), 'YYYY-MM-dd_H-mm-ss');

    let fileName = 'Mätarställning_' + this.selectedMeteringPoint.id;
    if (this.datePeriodType === DatePeriodType.Day) {
      fileName += `_dygn_${timestamp}.xlsx`;
    }
    if (this.datePeriodType === DatePeriodType.Month) {
      fileName += `_månad_${timestamp}.xlsx`;
    }

    this.subs.sink = this.exportService.exportInFrame(
      this.meteringPointService.createReportKey(),
      (key: string) => this.meteringPointService.exportReadingsToExcel(key, exportParameters),
      () => {
        this.isExporting = false;
        this.changeDetectorRef.detectChanges();
      },
      fileName
    );
  };
}
